/**
 * The latest module permissions, readable synchronously.
 *
 * ## Why this exists
 *
 * Navigation visibility is decided by a `hidden` predicate on each `FuseNavigationItem`, and that
 * predicate is **synchronous**: Fuse evaluates it inline in the template
 * (`*ngIf="(item.hidden && !item.hidden(item)) || !item.hidden"`). Permissions arrive over HTTP.
 * There is no way for a predicate to await them, so something has to hold the last known answer in a
 * plain variable that the predicate can read.
 *
 * That template evaluation is also what makes the timing safe. Because the predicate re-runs on every
 * change-detection pass rather than once at load, an item hidden while permissions are still unknown
 * appears by itself the moment they arrive. So the honest default is to HIDE what we cannot yet
 * confirm: an item that flickers in a moment after sign-in is a much smaller problem than one that
 * invites a click and answers with a 403.
 *
 * ## What this is NOT
 *
 * **This is a UI hint, never a control.** Every route it hides is independently enforced by the
 * backend - `plugins/permission` gates the HTTP prefix and refuses regardless of what the client
 * believes - and by `PermissionGuard` on the way in. Nothing here is load-bearing for security, and
 * nothing that decides access should ever read it. It exists so a person is not shown a door they
 * cannot open.
 *
 * A module-level variable rather than a service, because the only consumer is a predicate declared in
 * `mock-api/common/navigation/data.ts`, which is a plain data file with no injector around it.
 */

/** The permission payload shape, as `UserService` normalises it. */
export type ModulePermissionSnapshot = Record<string, unknown>;

let snapshot: ModulePermissionSnapshot | null = null;

/**
 * Record the permissions just resolved. Called by `UserService` on every successful load.
 *
 * Not merged with what was there: a fresh payload is the whole truth, and merging would let a
 * permission that has been REVOKED survive in the snapshot because the new payload no longer
 * mentions it.
 */
export function setModulePermissionSnapshot(payload: ModulePermissionSnapshot | null): void {
    snapshot = payload;
}

/**
 * Whether the signed-in caller currently has this module.
 *
 * Returns `false` while permissions are unknown, which is the fail-closed direction and the one that
 * matters here: see the file comment for why hiding-then-revealing beats revealing-then-refusing.
 *
 * Accepts the two shapes the payload uses. `UserService` normalises the keys it knows about to
 * booleans, and passes every other key through as the raw level string it arrived as, so a reader
 * has to understand both or it will read `'full_access'` as merely truthy on one path and miss it
 * entirely on another.
 */
export function hasModulePermission(key: string): boolean {
    const value = snapshot?.[key];
    if (typeof value === 'boolean') {
        return value;
    }
    const level = String(value ?? '')
        .trim()
        .toLowerCase();
    return level === 'full_access' || level === 'read_only';
}

/**
 * Synchronous record-scope read for UI that cannot await `getUserPermissions`
 * (same timing model as `hasModulePermission`).
 */
export function getModuleRecordScope(
    featureKey: string,
    fallback: 'own' | 'team' | 'all' = 'own'
): 'own' | 'team' | 'all' {
    const scopes = snapshot?.['record_scopes'];
    if (!scopes || typeof scopes !== 'object' || Array.isArray(scopes)) {
        return fallback;
    }
    const raw = String((scopes as Record<string, unknown>)[featureKey] ?? '')
        .trim()
        .toLowerCase();
    if (raw === 'own' || raw === 'team' || raw === 'all') {
        return raw;
    }
    return fallback;
}
